import { Box, Typography } from '@mui/material';
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export type RevenueAndTicketDatum = {
  label: string;
  date: string;
  revenue: number;
  ticketTotal: number;
  remainingUserTicket: number;
  newUserTicket: number;
};

const REVENUE_COLOR = '#64B5F6';
const TICKET_COLOR = '#F48FB1';
const REVENUE_GRADIENT_ID = 'colorRevenue';
const TICKET_GRADIENT_ID = 'colorTicket';

type Props = {
  data: RevenueAndTicketDatum[];
};

export default function RouletteRevenueAndTicketChart({ data }: Props) {
  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: 3,
        p: 3,
        mt: 3,
        border: '1px solid #f1f5f9',
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={2}
        textAlign="center"
        color="#475569"
      >
        일별 매출&티켓 추이
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 8, right: 48, left: 8, bottom: 8 }}>
          <defs>
            <linearGradient id={REVENUE_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={REVENUE_COLOR} stopOpacity={0.25} />
              <stop offset="95%" stopColor={REVENUE_COLOR} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={TICKET_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={TICKET_COLOR} stopOpacity={0.25} />
              <stop offset="95%" stopColor={TICKET_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="left"
            tickFormatter={(v) => (v >= 10000 ? `${(v / 10000).toFixed(0)}만` : v.toLocaleString('ko-KR'))}
            tick={{ fontSize: 11 }}
            label={{ value: '매출', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(v) => (v >= 10000 ? `${(v / 10000).toFixed(0)}만` : v.toLocaleString('ko-KR'))}
            tick={{ fontSize: 11 }}
            label={{ value: '티켓 발급 수', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload as RevenueAndTicketDatum;
              if (!row) return null;
              return (
                <Box
                  sx={{
                    p: 1.5,
                    minWidth: 180,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    {row.date}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    매출: ₩{row.revenue.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 0.25 }}>
                    티켓 발급 수
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 1 }}>
                    기존 사용자: {row.remainingUserTicket.toLocaleString('ko-KR')}장
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 1 }}>
                    신규 사용자: {row.newUserTicket.toLocaleString('ko-KR')}장
                  </Typography>
                </Box>
              );
            }}
          />
          <Legend wrapperStyle={{ paddingTop: 8 }} />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="revenue"
            stroke={REVENUE_COLOR}
            strokeWidth={2}
            fill={`url(#${REVENUE_GRADIENT_ID})`}
            fillOpacity={1}
            name="매출"
          />
          <Area
            yAxisId="right"
            type="monotone"
            dataKey="ticketTotal"
            stroke={TICKET_COLOR}
            strokeWidth={2}
            fill={`url(#${TICKET_GRADIENT_ID})`}
            fillOpacity={1}
            name="티켓 발급 수"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
